import {
    Features,
    useFeatureFlagValue,
} from '@experiences/feature-flags';
import { PolicyEnforcement } from '@experiences/interfaces';
import {
    SpacingToken,
    UiCircularProgress,
    UiStack,
    UiText,
} from '@experiences/ui-common';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
    Box,
    Chip,
} from '@mui/material';
import { FontVariantToken } from '@uipath/apollo-core';
import { ApIconButton } from '@uipath/portal-shell-react';
import React from 'react';
import { useIntl } from 'react-intl';

import type { DisplayItem } from '../../governanceConstants';
import useGovernancePolicyPreviewViewModel from './GovernancePolicyPreviewViewModel';

interface GovernancePolicyPreviewProps {
    onEditResources?: () => void;
    onEditAutomations?: () => void;
    onEditDecision?: () => void;
    onEditNameAndDescription?: () => void;
    onEditEnforcement?: () => void;
    mode?: 'create' | 'edit';
}

const cardSx = {
    backgroundColor: 'var(--color-background-default)',
    border: '1px solid var(--color-border-de-emp)',
    borderRadius: '12px',
    padding: '24px',
} as const;

const labelSx = {
    minWidth: '160px',
    flexShrink: 0,
    color: 'var(--color-foreground-de-emp)',
} as const;

// ComponentType<any> rather than ElementType: passing `sx` to an ElementType union makes
// native TS7 produce a union type too complex to represent (TS2590).
const accessDecisionConfig: Record<PolicyEnforcement, { color: string; Icon: React.ComponentType<any> }> = {
    [PolicyEnforcement.Allow]: {
        color: 'var(--color-success-foreground)',
        Icon: CheckCircleOutlineIcon,
    },
    [PolicyEnforcement.Deny]: {
        color: 'var(--color-error-foreground)',
        Icon: CancelOutlinedIcon,
    },
    [PolicyEnforcement.Prompt]: {
        color: 'var(--color-foreground-brand)',
        Icon: AccessTimeIcon,
    },
    [PolicyEnforcement.NoOp]: {
        color: 'var(--color-foreground-de-emp)',
        Icon: BlockOutlinedIcon,
    },
};

// ─── Shared sub-components ───────────────────────────────────────────────────

const PreviewCardHeader: React.FC<{
    title: string;
    onEdit?: () => void;
    editDataCy?: string;
    titleDataCy?: string;
    showEdit?: boolean;
}> = ({
    title, onEdit, editDataCy, titleDataCy, showEdit = true,
}) => (
    <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: '16px',
    }}>
        <UiText
            variant={FontVariantToken.fontSizeMBold}
            data-cy={titleDataCy}
        >
            {title}
        </UiText>
        {onEdit && showEdit && (
            <ApIconButton
                onClick={onEdit}
                data-cy={editDataCy}
                style={{ color: 'var(--color-foreground-brand)' }}
            >
                <EditOutlinedIcon />
            </ApIconButton>
        )}
    </Box>
);

const LabelValueRow: React.FC<{
    label: string;
    labelDataCy?: string;
    children: React.ReactNode;
}> = ({
    label, labelDataCy, children,
}) => (
    <Box sx={{
        display: 'flex',
        gap: '16px',
        alignItems: 'flex-start',
    }}>
        <UiText
            variant={FontVariantToken.fontSizeM}
            style={labelSx}
            data-cy={labelDataCy}>
            {label}
        </UiText>
        {children}
    </Box>
);

interface RuleDisplayData {
    items: DisplayItem[];
    tags: string[];
    tagLogicText: string;
}

const RuleDisplaySection: React.FC<{
    display: RuleDisplayData | null;
    itemsLabel: string;
    itemsLabelDataCy?: string;
    emptyKey: string;
    emptyDataCy?: string;
}> = ({
    display, itemsLabel, itemsLabelDataCy, emptyKey, emptyDataCy,
}) => {
    const { formatMessage: translate } = useIntl();
    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
        }}>
            {/* Items row */}
            <LabelValueRow
                label={itemsLabel}
                labelDataCy={itemsLabelDataCy}>
                <Box sx={{ flex: 1 }}>
                    {display && display.items.length > 0 ? (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '6px',
                        }}>
                            {display.items.map((item, idx) => (
                                <Box key={idx}>
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                    }}>
                                        <CheckIcon
                                            sx={{
                                                fontSize: '16px',
                                                color: 'var(--color-success-foreground)',
                                                flexShrink: 0,
                                            }}
                                        />
                                        <UiText variant={FontVariantToken.fontSizeM}>
                                            {item.text}
                                        </UiText>
                                    </Box>
                                    {item.values && item.values.length > 0 && (
                                        <Box sx={{
                                            ml: '22px',
                                            mt: '4px',
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: '4px',
                                        }}>
                                            {item.values.map((v, vi) => (
                                                <Chip
                                                    key={vi}
                                                    label={v}
                                                    size="small" />
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            ))}
                        </Box>
                    ) : (
                        <UiText
                            variant={FontVariantToken.fontSizeM}
                            style={{ color: 'var(--color-foreground-de-emp)' }}
                            data-cy={emptyDataCy}
                        >
                            {translate({ id: emptyKey })}
                        </UiText>
                    )}
                </Box>
            </LabelValueRow>
            {/* Tags row */}
            <LabelValueRow label={translate({ id: 'CLIENT_GOVERNANCE_REVIEW_TAGS_LABEL' })}>
                <Box sx={{ flex: 1 }}>
                    {display && display.tags.length > 0 ? (
                        <Box sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '4px',
                            alignItems: 'center',
                        }}>
                            <UiText
                                variant={FontVariantToken.fontSizeM}
                                style={{
                                    color: 'var(--color-foreground-de-emp)',
                                    marginRight: '4px',
                                }}
                            >
                                {display.tagLogicText}
                            </UiText>
                            {display.tags.map((tag, ti) => (
                                <Chip
                                    key={ti}
                                    label={tag}
                                    size="small" />
                            ))}
                        </Box>
                    ) : (
                        <UiText
                            variant={FontVariantToken.fontSizeM}
                            style={{ color: 'var(--color-foreground-de-emp)' }}
                        >
                            {translate({ id: 'CLIENT_GOVERNANCE_REVIEW_NO_TAG_FILTER' })}
                        </UiText>
                    )}
                </Box>
            </LabelValueRow>
        </Box>
    );
};

// ─── Main component ──────────────────────────────────────────────────────────

export const GovernancePolicyPreview: React.FC<GovernancePolicyPreviewProps> = ({
    onEditResources,
    onEditAutomations,
    onEditDecision,
    onEditNameAndDescription,
    onEditEnforcement,
    mode = 'create',
}) => {
    const { formatMessage: translate } = useIntl();
    const EnableGovernanceAccessDecisionEdit = useFeatureFlagValue(Features.EnableGovernanceAccessDecisionEdit.name);

    const {
        scopeDisplay,
        resourcesDisplay,
        automationsDisplay,
        actorIdentityDisplay,
        accessDecisionDisplay,
        enforcement,
        enforcementModeDisplay,
        enforcementModeDescription,
        name,
        description,
        isLoadingNames,
    } = useGovernancePolicyPreviewViewModel();

    if (isLoadingNames) {
        return (
            <Box sx={{
                padding: '24px',
                height: '100%',
            }}>
                <UiStack
                    direction="column"
                    align="center"
                    justify="center"
                    h="100%"
                    data-cy="governance-policy-review-loading"
                >
                    <UiCircularProgress />
                </UiStack>
            </Box>
        );
    }

    const decisionConfig = enforcement ? accessDecisionConfig[enforcement] : null;

    return (
        <UiStack
            direction="column"
            gap={SpacingToken.XL}
            data-cy="governance-policy-review-panel"
        >
            {/* Policy Setup */}
            <Box sx={cardSx}>
                <PreviewCardHeader
                    title={translate({ id: 'CLIENT_GOVERNANCE_REVIEW_POLICY_SETUP' })}
                    titleDataCy="governance-policy-review-scope-label"
                    showEdit={false}
                />
                <LabelValueRow label={translate({ id: 'CLIENT_GOVERNANCE_SCOPE_TENANT_LABEL' })}>
                    <UiText
                        variant={FontVariantToken.fontSizeM}
                        data-cy="governance-policy-review-scope-value"
                    >
                        {scopeDisplay()}
                    </UiText>
                </LabelValueRow>
            </Box>

            {/* Target Resource */}
            <Box sx={cardSx}>
                <PreviewCardHeader
                    title={translate({ id: 'CLIENT_GOVERNANCE_REVIEW_TARGET_RESOURCE' })}
                    onEdit={onEditResources}
                    editDataCy="governance-policy-review-edit-resources-button"
                />
                <RuleDisplaySection
                    display={resourcesDisplay}
                    itemsLabel={translate({ id: 'CLIENT_GOVERNANCE_REVIEW_RESOURCES_LABEL' })}
                    itemsLabelDataCy="governance-policy-review-resources-label"
                    emptyKey="CLIENT_GOVERNANCE_POLICY_DEFINITION_NO_RESOURCES_SELECTED"
                    emptyDataCy="governance-policy-review-resources-empty"
                />
            </Box>

            {/* Actor Process */}
            <Box sx={cardSx}>
                <PreviewCardHeader
                    title={translate({ id: 'CLIENT_GOVERNANCE_REVIEW_ACTOR_PROCESS' })}
                    onEdit={onEditAutomations}
                    editDataCy="governance-policy-review-edit-automations-button"
                />
                <RuleDisplaySection
                    display={automationsDisplay}
                    itemsLabel={translate({ id: 'CLIENT_GOVERNANCE_REVIEW_PROCESS_TYPES_LABEL' })}
                    itemsLabelDataCy="governance-policy-review-automations-label"
                    emptyKey="CLIENT_GOVERNANCE_POLICY_DEFINITION_NO_AUTOMATIONS_SELECTED"
                    emptyDataCy="governance-policy-review-automations-empty"
                />
            </Box>

            {/* Actor Identity */}
            <Box sx={cardSx}>
                <PreviewCardHeader
                    title={translate({ id: 'CLIENT_GOVERNANCE_REVIEW_ACTOR_IDENTITY' })}
                    onEdit={onEditAutomations}
                    editDataCy="governance-policy-review-edit-actor-identity-button"
                />
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                }}>
                    {/* Identity types row */}
                    <LabelValueRow
                        label={translate({ id: 'CLIENT_GOVERNANCE_REVIEW_IDENTITY_TYPES_LABEL' })}
                        labelDataCy="governance-policy-review-actor-identity-label">
                        <Box sx={{ flex: 1 }}>
                            {actorIdentityDisplay && actorIdentityDisplay.items.length > 0 ? (
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '6px',
                                }}>
                                    {actorIdentityDisplay.items.map((item, idx) => (
                                        <Box key={idx}>
                                            <Box sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                            }}>
                                                <CheckIcon
                                                    sx={{
                                                        fontSize: '16px',
                                                        color: 'var(--color-success-foreground)',
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                <UiText variant={FontVariantToken.fontSizeM}>
                                                    {item.text}
                                                </UiText>
                                            </Box>
                                            {item.values && item.values.length > 0 && (
                                                <Box sx={{
                                                    ml: '22px',
                                                    mt: '4px',
                                                    display: 'flex',
                                                    flexWrap: 'wrap',
                                                    gap: '4px',
                                                }}>
                                                    {item.values.map((v, vi) => (
                                                        <Chip
                                                            key={vi}
                                                            label={v}
                                                            size="small" />
                                                    ))}
                                                </Box>
                                            )}
                                        </Box>
                                    ))}
                                </Box>
                            ) : (
                                <UiText
                                    variant={FontVariantToken.fontSizeM}
                                    style={{ color: 'var(--color-foreground-de-emp)' }}
                                    data-cy="governance-policy-review-actor-identity-empty"
                                >
                                    {translate({ id: 'CLIENT_GOVERNANCE_REVIEW_NO_ACTOR_IDENTITY_SELECTED' })}
                                </UiText>
                            )}
                        </Box>
                    </LabelValueRow>
                </Box>
            </Box>

            {/* Access Decision */}
            <Box sx={cardSx}>
                <PreviewCardHeader
                    title={translate({ id: 'CLIENT_GOVERNANCE_REVIEW_ACCESS_DECISION' })}
                    onEdit={onEditDecision}
                    editDataCy="governance-policy-review-edit-decision-icon-button"
                    titleDataCy="governance-policy-review-decision-label"
                    showEdit={!!EnableGovernanceAccessDecisionEdit}
                />
                {decisionConfig && accessDecisionDisplay ? (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                        }}
                        data-cy="governance-policy-review-decision-value"
                    >
                        <decisionConfig.Icon sx={{
                            color: decisionConfig.color,
                            fontSize: '20px',
                        }} />
                        <UiText
                            variant={FontVariantToken.fontSizeMBold}
                            style={{ color: decisionConfig.color }}
                        >
                            {accessDecisionDisplay}
                        </UiText>
                    </Box>
                ) : (
                    <UiText
                        variant={FontVariantToken.fontSizeM}
                        style={{ color: 'var(--color-foreground-de-emp)' }}
                        data-cy="governance-policy-review-decision-value"
                    >
                        {translate({ id: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_NO_ACCESS_DECISION_SELECTED' })}
                    </UiText>
                )}
            </Box>

            {/* Name and Description */}
            <Box sx={cardSx}>
                <PreviewCardHeader
                    title={translate({ id: 'CLIENT_GOVERNANCE_NAME_AND_DESCRIPTION' })}
                    onEdit={onEditNameAndDescription}
                    editDataCy="governance-policy-review-edit-name-description-button"
                    titleDataCy="governance-policy-review-name-description-label"
                />
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                }}>
                    <LabelValueRow
                        label={translate({ id: 'CLIENT_GOVERNANCE_POLICY_NAME_LABEL' })}
                        labelDataCy="governance-policy-review-policy-name-label">
                        <UiText
                            variant={FontVariantToken.fontSizeM}
                            data-cy="governance-policy-review-policy-name-value"
                        >
                            {name || translate({ id: 'CLIENT_GOVERNANCE_POLICY_NAME_PLACEHOLDER' })}
                        </UiText>
                    </LabelValueRow>
                    <LabelValueRow
                        label={translate({ id: 'CLIENT_GOVERNANCE_POLICY_DESCRIPTION_LABEL' })}
                        labelDataCy="governance-policy-review-description-label">
                        <UiText
                            variant={FontVariantToken.fontSizeM}
                            style={!description ? { color: 'var(--color-foreground-de-emp)' } : undefined}
                            data-cy="governance-policy-review-description-value"
                        >
                            {description || translate({ id: 'CLIENT_GOVERNANCE_POLICY_DESCRIPTION_PLACEHOLDER' })}
                        </UiText>
                    </LabelValueRow>
                </Box>
            </Box>

            {/* Enforcement Mode */}
            <Box sx={cardSx}>
                <PreviewCardHeader
                    title={translate({ id: 'CLIENT_GOVERNANCE_STEP_ENFORCEMENT_MODE' })}
                    onEdit={onEditEnforcement}
                    editDataCy="governance-policy-review-edit-enforcement-button"
                    titleDataCy="governance-policy-review-enforcement-mode-label"
                />
                <LabelValueRow label={translate({ id: 'CLIENT_GOVERNANCE_REVIEW_MODE_LABEL' })}>
                    <UiText
                        variant={FontVariantToken.fontSizeM}
                        data-cy="governance-policy-review-enforcement-mode-value"
                    >
                        {enforcementModeDisplay ?? translate({ id: 'CLIENT_GOVERNANCE_ENFORCEMENT_MODE_SIMULATION_TITLE' })}
                        {enforcementModeDescription && (
                            <Box
                                component="span"
                                sx={{ color: 'var(--color-foreground-de-emp)' }}
                            >
                                {' \u2014 '}
                                {enforcementModeDescription}
                            </Box>
                        )}
                    </UiText>
                </LabelValueRow>
            </Box>
        </UiStack>
    );
};
